import { useEffect } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

/**
 * Detects deep links of the form /opportunity/:id/:diagramId?organization_id=<uuid>&slug=<slug>
 * and enforces authentication before allowing access.
 *
 * Behaviour:
 * - If the current URL matches /opportunity/:id/:diagramId pattern:
 *   - Extract and store slug from query params if present
 *   - If the user is NOT authenticated:
 *     - Store the full target URL in sessionStorage under `postLoginRedirect`.
 *     - Redirect the user to the login route with the appropriate tenant slug.
 *   - If the user IS authenticated:
 *     - Allow access (component will handle organization_id and slug from query params).
 *
 * Usage:
 * - Call this hook in AppLayout so it runs on load and when auth state changes.
 */
export const useOpportunityAuthRedirect = (): void => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ id: string; diagramId: string }>();
  const [searchParams] = useSearchParams();

  const isAuthenticated = useSelector(
    (state: RootState) => state.user?.isLoggedIn ?? state.auth?.isAuthenticated ?? false
  );

  useEffect(() => {
    // Check if we're on the opportunity route with both id and diagramId
    const isOpportunityRoute = params.id && params.diagramId;

    if (!isOpportunityRoute) {
      return;
    }

    // Extract slug from query params FIRST (before any redirect)
    const slugFromQuery = searchParams.get('slug');

    // If slug is in query params, store it immediately
    let slug = localStorage.getItem('tenant-slug') || 'thehackettgroup';
    if (slugFromQuery) {
      localStorage.setItem('tenant-slug', slugFromQuery);
      slug = slugFromQuery;
    }

    const targetUrl = `${location.pathname}${location.search}`;

    if (!isAuthenticated) {
      // Persist intended target so login can redirect back afterwards
      try {
        sessionStorage.setItem('postLoginRedirect', targetUrl);
      } catch {
        // ignore storage errors
      }

      // Redirect to login with the correct tenant slug
      navigate(`/${slug}/login`, { replace: true });
    }
  }, [
    isAuthenticated,
    location.pathname,
    location.search,
    navigate,
    params.id,
    params.diagramId,
    searchParams,
  ]);
};
